import * as React from "react";
import { EventResource } from "client/resources";
import { memoize } from "lodash";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import {parameterizedRouteLinks} from "../../routeLinks";
import {EventReference} from "../../client/resources/eventResource";

interface EventFormatterProps {
    event: EventResource;
}

interface ResourceLinkItem {
    prefix: string;
    redirect(id: string): string;
}

interface Index {
    reference?: EventReference;
    index: number;
}

interface Part {
    reference?: EventReference;
    message: string;
}

const documentTypeToLinkLookups = memoize((spaceId?: string) => linksFor(spaceId));

function linkTo(documentId: string, documentType: string, spaceId?: string) {
    if (!documentId || !documentType) {
        return "";
    }

    const documentTypeSpecificLink = documentTypeToLinkLookups(spaceId)[documentType];
    if (!documentTypeSpecificLink) {
        return "/not-found";
    }

    return documentTypeSpecificLink(documentId);
}

export const EventFormatter: React.StatelessComponent<EventFormatterProps> = props => {
    const message = props.event.Message;
    const references = props.event.MessageReferences;

    const indexes: Index[] = !references || references.length === 0 ? [{ reference: null, index: 0 }, { reference: null, index: message.length }]
        : toIndexes(references, message);

    let last: Index = null;
    const parts: Part[] = [];
    indexes.map(index => {
        if (last) {
            parts.push({ reference: last.reference, message: message.substring(last.index, index.index) });
        }
        last = index;
    });

    const encodedParts = parts.map(part => {
        const encodedMessage = <span>{part.message}</span>;
        if (part.reference) {
            const documentId = part.reference.ReferencedDocumentId;
            const documentType = retrieveDocumentTypeFromId(documentId);
            if (documentType !== "interruption") {
                return <InternalLink to={linkTo(documentId, documentType, "")}>{encodedMessage}</InternalLink>;
            } else {
                return encodedMessage;
            }
        } else {
            return encodedMessage;
        }
    });

    return (
        <span>
            {React.Children.toArray(encodedParts)}
        </span>
    );
};

function retrieveDocumentTypeFromId(documentId: string): string {
    const index = documentId.indexOf("-");
    return index === -1 ? null : documentId.substr(0, index).toLowerCase();
}

function toIndexes(references: EventReference[], message: string) {
    const indexes: Index[] = [];
    const refStart = references[0];

    if (refStart.StartIndex !== 0) {
        indexes.push({ reference: null, index: 0 });
    }

    let lastRef: EventReference = null;
    references.forEach(reference => {
        if (!lastRef || lastRef.StartIndex !== reference.StartIndex) {
            indexes.push({ reference, index: reference.StartIndex });
        }
        indexes.push({ reference: null, index: reference.StartIndex + reference.Length });
        lastRef = reference;
    });

    const refEnd = references[references.length - 1];
    const endIndex = (refEnd.StartIndex + refEnd.Length);
    if (endIndex !== message.length) {
        indexes.push({ reference: null, index: message.length });
    }

    return indexes;
}

type DocumentTypeToLinkLookup = { [prefix: string]: (id: string) => string; };

function linksFor(spaceId?: string): DocumentTypeToLinkLookup {
    const lookup: DocumentTypeToLinkLookup = {};

    const routeLinks = parameterizedRouteLinks(spaceId ? spaceId : "");

    lookup["actiontemplates"]  = (id) => routeLinks.library.stepTemplate(id).root;
    lookup["deployments"]  =  (id) => routeLinks.deployment(id).root;
    lookup["environments"]  =  (id) => routeLinks.infrastructure.environment(id);
    lookup["feeds"]  =  (id) => routeLinks.library.feed(id).root;
    lookup["libraryvariablesets"]  =  (id) => routeLinks.library.variableSet(id);
    lookup["lifecycles"]  =  (id) => routeLinks.library.lifecycle(id);
    lookup["machinepolicies"]  =  (id) => routeLinks.infrastructure.machinePolicy(id);
    lookup["machines"]  =  (id) => routeLinks.infrastructure.machine(id).root;
    lookup["projects"]  =  (id) => routeLinks.project(id).root;
    lookup["projectgroups"]  =  (id) => routeLinks.projectGroup(id).root;
    lookup["proxys"]  =  (id) => routeLinks.infrastructure.proxy(id);
    lookup["proxies"]  =  (id) => routeLinks.infrastructure.proxy(id);
    lookup["releases"]  =  (id) => routeLinks.release(id);
    lookup["servertasks"]  =  (id) => routeLinks.task(id).root;
    lookup["tagsets"]  =  (id) => routeLinks.library.tagSet(id);
    lookup["teams"]  =  (id) => routeLinks.configuration.team(id);
    lookup["tenants"]  =  (id) => routeLinks.tenant(id).root;
    lookup["users"]  =  (id) => routeLinks.configuration.user(id);
    lookup["subscriptions"]  =  (id) => routeLinks.configuration.subscription(id);
    lookup["certificates"]  =  (id) => routeLinks.library.certificate(id);
    lookup["projecttriggers"]  =  (id) => routeLinks.trigger(id);
    lookup["channels"]  =  (id) => routeLinks.channel(id);
    return lookup;
}

export default EventFormatter;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/EventFormatter/EventFormatter.tsx